if (!customElements.get('ate-headwear-cart')) {
  customElements.define('ate-headwear-cart', class extends HTMLElement {
    connectedCallback() {
      this.data = JSON.parse(this.querySelector('[data-hw-cart-data]').textContent);
      this.onChange = event => {
        const row = event.target.closest('[data-ate-hw-role]');
        if (!row || !['headwear', 'patch', 'application'].includes(row.dataset.ateHwRole) || !event.target.matches('input[name="updates[]"]')) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.changeBundle(row.dataset.ateHwBundle, Number(event.target.value));
      };
      this.onClick = event => {
        const remove = event.target.closest('cart-remove-button');
        const row = remove?.closest('[data-ate-hw-role]');
        if (!row || !['headwear', 'patch', 'application'].includes(row.dataset.ateHwRole)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.changeBundle(row.dataset.ateHwBundle, 0);
      };
      this.onSubmit = event => {
        if (event.target.id === 'cart' && !this.validate()) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      };
      document.addEventListener('change', this.onChange, true);
      document.addEventListener('click', this.onClick, true);
      document.addEventListener('submit', this.onSubmit, true);
      this.observer = new MutationObserver(() => this.validate());
      this.observer.observe(document.body, { childList: true, subtree: true });
      this.validate();
    }
    disconnectedCallback() {
      document.removeEventListener('change', this.onChange, true);
      document.removeEventListener('click', this.onClick, true);
      document.removeEventListener('submit', this.onSubmit, true);
      this.observer?.disconnect();
    }
    groups(lines) {
      const groups = new Map();
      for (const line of lines) {
        if (!line.nonce || !['headwear', 'patch', 'application'].includes(line.role)) continue;
        if (!groups.has(line.nonce)) groups.set(line.nonce, []);
        groups.get(line.nonce).push(line);
      }
      return groups;
    }
    complete(lines) {
      if (lines.length !== 3 || ['headwear', 'patch', 'application'].some(role => lines.filter(line => line.role === role).length !== 1)) return false;
      const quantity = lines[0].quantity;
      const application = lines.find(line => line.role === 'application');
      return Number.isInteger(quantity) && quantity > 0 && lines.every(line => line.quantity === quantity) && String(application.variantId) === String(this.data.applicationId) && application.finalPrice === this.data.applicationPrice;
    }
    validate() {
      const groups = this.groups(this.data.lines);
      const invalid = this.busy || [...groups.values()].some(lines => !this.complete(lines));
      const error = this.querySelector('[data-hw-cart-error]');
      const message = this.busy ? this.data.messages.updating : this.data.messages.invalid;
      if (error.textContent !== message) error.textContent = message;
      error.hidden = !invalid;
      this.querySelector('[data-hw-cart-note]').hidden = !groups.size;
      document.querySelectorAll('[name="checkout"]').forEach(button => {
        if (invalid && !button.disabled) {
          button.dataset.ateHwGuardDisabled = 'true';
          button.disabled = true;
        } else if (!invalid && button.dataset.ateHwGuardDisabled === 'true') {
          button.disabled = false;
          delete button.dataset.ateHwGuardDisabled;
        }
      });
      document.querySelectorAll('.cart__dynamic-checkout-buttons').forEach(element => {
        if (invalid && element.dataset.ateHwGuardDisplay === undefined) {
          element.dataset.ateHwGuardDisplay = element.style.display;
          element.style.display = 'none';
        } else if (!invalid && element.dataset.ateHwGuardDisplay !== undefined) {
          element.style.display = element.dataset.ateHwGuardDisplay;
          delete element.dataset.ateHwGuardDisplay;
        }
      });
      return !invalid;
    }
    async cart() {
      const response = await fetch(this.dataset.cartUrl + '.js', { cache: 'no-store' });
      if (!response.ok) throw new Error(this.data.messages.invalid);
      return response.json();
    }
    async changeBundle(nonce, quantity) {
      if (this.busy || !nonce || !Number.isInteger(quantity) || quantity < 0 || quantity > 10000) return;
      this.busy = true;
      this.validate();
      this.closest('cart-items').querySelectorAll('input,button').forEach(control => control.disabled = true);
      let original = [];
      try {
        let cart = await this.cart();
        original = cart.items.filter(line => line.properties?._hat_patch_bundle === nonce && line.properties?._ate_bundle_role).map(line => ({ role: line.properties._ate_bundle_role, quantity: line.quantity }));
        const roles = cart.items.filter(line => line.properties?._hat_patch_bundle === nonce && line.properties?._ate_bundle_role).map(line => line.properties._ate_bundle_role);
        if (!roles.length || (quantity > 0 && (roles.length !== 3 || new Set(roles).size !== 3))) throw new Error(this.data.messages.invalid);
        for (const role of roles) {
          const line = cart.items.find(item => item.properties?._hat_patch_bundle === nonce && item.properties?._ate_bundle_role === role);
          if (!line) throw new Error(this.data.messages.invalid);
          const response = await fetch(this.dataset.cartUrl + '/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: line.key, quantity }) });
          if (!response.ok) throw new Error(this.data.messages.invalid);
          cart = await response.json();
          if (cart.status) throw new Error(this.data.messages.invalid);
        }
        const remaining = cart.items.filter(line => line.properties?._hat_patch_bundle === nonce);
        const normalized = remaining.map(line => ({ role: line.properties._ate_bundle_role, quantity: line.quantity, variantId: line.variant_id, finalPrice: line.final_price }));
        if (quantity === 0 ? remaining.length !== 0 : !this.complete(normalized)) throw new Error(this.data.messages.invalid);
      } catch {
        if (quantity > 0 && original.length === 3) {
          try {
            let cart = await this.cart();
            for (const previous of original) {
              const line = cart.items.find(item => item.properties?._hat_patch_bundle === nonce && item.properties?._ate_bundle_role === previous.role);
              if (!line || line.quantity === previous.quantity) continue;
              const response = await fetch(this.dataset.cartUrl + '/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: line.key, quantity: previous.quantity }) });
              if (!response.ok) break;
              cart = await response.json();
            }
          } catch {}
        }
      } finally {
        window.location.assign(this.dataset.cartUrl);
      }
    }
  });
}
